import { inject, Injectable } from '@angular/core';
import {
    Actions,
    ofActionErrored,
    ofActionSuccessful,
    Store,
} from '@ngxs/store';
import {
    map,
    Observable,
} from 'rxjs';

import { SimulationHistoricalState } from '~/app/core/state/simulation-historical/simulation-historical.state';
import { EntityType } from '~/app/shared/enums/entity-type.enum';
import { Period } from '~/app/shared/enums/period.enum';
import { SimulationHistoricalPeriodType } from '~/app/domains/simulation/enums/simulation-historical-period-type.enum';
import { SimulationHistoricalViewModeValue } from '~/app/domains/simulation/enums/simulation-historical-view-mode-value.enum';
import { ViewModeType } from '~/app/shared/enums/view-mode-type.enum';
import { ComparedEntity } from '~/app/shared/types/compared-entity.type';
import { EntityToLoad } from '~/app/shared/types/entity-to-load.type';
import { OperationSimulationUpdatingPortfolio } from '~/app/shared/types/operations/operation-simulation-updating-portfolio.type';
import { CreatePortfolio } from '~/app/domains/portfolios/types/create-portfolio.type';
import { Portfolio } from '~/app/domains/portfolios/types/portfolio.type';
import { ProjectionAllocation } from '~/app/domains/simulation/types/projection-allocation.type';
import { ShareCategory } from '~/app/domains/shares/types/share-category.type';
import { Share } from '~/app/domains/shares/types/share.type';
import {
    SimulationCommonBenchmarkParam,
} from '~/app/domains/simulation/types/simulation-common/simulation-common-benchmark-param.type';
import { SimulationCommonReferenceRequest } from '~/app/domains/simulation/types/simulation-common/simulation-common-reference-request.type';
import { SimulationHistoricalPeriodParam } from '~/app/domains/simulation/types/simulation-historical/simulation-historical-period-param.type';
import { SimulationHistoricalResponse } from '~/app/domains/simulation/types/simulation-historical/simulation-historical-response.type';

import {
    AddAllocationsToComparedEntitiesAction,
    AddCategoriesToComparedEntitiesAction,
    AddComparisonListToComparedEntitiesAction,
    AddCreatingPortfolioToComparedEntitiesAction, AddCurrentAllocationToComparedEntitiesAction,
    AddPortfoliosToComparedEntitiesAction,
    AddSharesToComparedEntitiesAction,
    CancelLaunchSimulationAction,
    ChangeViewModeAction,
    CopyActiveInDraftAction,
    DeleteAllComparedEntitiesAction,
    DeleteComparedEntityAction,
    LaunchSimulationAction,
    ResetAction,
    ResetViewModeDisplayAction,
    SetViewModeDisplayValuesAction,
    ToggleBenchmarkAction,
    UpdateDraftPeriodParamsAction,
} from './simulation-historical.actions';

@Injectable({
    providedIn: 'root',
})
export class SimulationHistoricalFacade {
    private store: Store = inject(Store);
    private actions$: Actions = inject(Actions);
    public draftPeriodParams$: Observable<SimulationHistoricalPeriodParam> = this.store.select(SimulationHistoricalState.getDraftPeriodParams);

    public draftComparedEntities$: Observable<ComparedEntity<Share | Portfolio | ShareCategory, OperationSimulationUpdatingPortfolio>[]> = this.store.select(SimulationHistoricalState.getDraftComparedEntities);

    public draftComparedAllocationCount$: Observable<number> = this.store.select(SimulationHistoricalState.getDraftComparedAllocationCount);

    public activeComparedEntities$: Observable<ComparedEntity<Share | Portfolio | ShareCategory, OperationSimulationUpdatingPortfolio>[]> = this.store.select(SimulationHistoricalState.getActiveComparedEntities);

    public draftBenchmark$: Observable<SimulationCommonBenchmarkParam> = this.store.select(SimulationHistoricalState.getDraftBenchmark);

    public draftComparedEntitiesCount$: Observable<number> = this.store.select(SimulationHistoricalState.getDraftComparedEntitiesCount);

    public requestId$: Observable<string | null> = this.store.select(SimulationHistoricalState.getRequestId);

    public simulation$: Observable<SimulationHistoricalResponse | null> = this.store.select(SimulationHistoricalState.getSimulation);

    public activePeriodParams$: Observable<SimulationHistoricalPeriodParam> = this.store.select(SimulationHistoricalState.getActivePeriodParams);

    public activeComparedEntitiesWithoutData$: Observable<ComparedEntity[]> = this.store.select(SimulationHistoricalState.getActiveComparedEntitiesWithoutData);

    public activePortfolios$: Observable<ComparedEntity<Portfolio>[]> = this.store.select(SimulationHistoricalState.getActivePortfolios);

    public activeAllocations$: Observable<ComparedEntity<Portfolio, OperationSimulationUpdatingPortfolio>[]> = this.store.select(SimulationHistoricalState.getActiveAllocations);

    public activePortfoliosWithoutData$: Observable<ComparedEntity[]> = this.store.select(SimulationHistoricalState.getActivePortfoliosWithoutData);

    public activeAllocationsWithoutData$: Observable<ComparedEntity[]> = this.store.select(SimulationHistoricalState.getActiveAllocationsWithoutData);

    public activeComparedEntitiesCount$: Observable<number> = this.store.select(SimulationHistoricalState.getActiveComparedEntitiesCount);

    public activeBenchmark$: Observable<SimulationCommonBenchmarkParam> = this.store.select(SimulationHistoricalState.getActiveBenchmark);

    public viewModeDisplay$: Observable<SimulationHistoricalViewModeValue[]> = this.store.select(SimulationHistoricalState.getViewModeDisplay);

    public viewModeParam$: Observable<ViewModeType> = this.store.select(SimulationHistoricalState.getViewModeParam);

    public viewModeDisplayPerf100$: Observable<boolean> = this.store.select(SimulationHistoricalState.isDisplayedInCurrentViewMode(SimulationHistoricalViewModeValue.PERF_100));

    public viewModeDisplayRiskIndicator$: Observable<boolean> = this.store.select(SimulationHistoricalState.isDisplayedInCurrentViewMode(SimulationHistoricalViewModeValue.RISK_INDICATOR_COMPARISON));

    public viewModeDisplayComparisonAnalysis$: Observable<boolean> = this.store.select(SimulationHistoricalState.isDisplayedInCurrentViewMode(SimulationHistoricalViewModeValue.COMPARISON_ANALYSIS));

    public viewModeDisplayCorrelation$: Observable<boolean> = this.store.select(SimulationHistoricalState.isDisplayedInCurrentViewMode(SimulationHistoricalViewModeValue.CORRELATION));

    public viewModeDisplayScatter$: Observable<boolean> = this.store.select(SimulationHistoricalState.isDisplayedInCurrentViewMode(SimulationHistoricalViewModeValue.SCATTER));

    public viewModeDisplayPortfoliosAllocation$: Observable<boolean> = this.store.select(SimulationHistoricalState.isDisplayedInCurrentViewMode(SimulationHistoricalViewModeValue.PORTFOLIOS_ALLOCATION));

    public viewModeDisplayPortfoliosContribution$: Observable<boolean> = this.store.select(SimulationHistoricalState.isDisplayedInCurrentViewMode(SimulationHistoricalViewModeValue.PORTFOLIOS_CONTRIBUTION));

    public viewModeDisplayPortfoliosDiversification$: Observable<boolean> = this.store.select(SimulationHistoricalState.isDisplayedInCurrentViewMode(SimulationHistoricalViewModeValue.PORTFOLIOS_DIVERSIFICATION));

    public viewModeDisplayPortfoliosFundAnalysis$: Observable<boolean> = this.store.select(SimulationHistoricalState.isDisplayedInCurrentViewMode(SimulationHistoricalViewModeValue.PORTFOLIOS_FUND_ANALYSIS));

    public viewModeDisplayPortfoliosScatter$: Observable<boolean> = this.store.select(SimulationHistoricalState.isDisplayedInCurrentViewMode(SimulationHistoricalViewModeValue.PORTFOLIOS_SCATTER));

    public onLaunchSimulationErrored(): Observable<unknown> {
        return this.actions$.pipe(
            ofActionErrored(LaunchSimulationAction),
        );
    }

    public addComparisonListToComparedEntities(listId: number) {
        return this.store.dispatch(new AddComparisonListToComparedEntitiesAction(listId));
    }

    public addSharesToComparedEntities(shares: EntityToLoad[]) {
        return this.store.dispatch(new AddSharesToComparedEntitiesAction(shares));
    }

    public addCategoriesToComparedEntities(categoriesIds: number[]) {
        return this.store.dispatch(new AddCategoriesToComparedEntitiesAction(categoriesIds));
    }

    public addPortfoliosToComparedEntities(portfolios: EntityToLoad[]) {
        return this.store.dispatch(new AddPortfoliosToComparedEntitiesAction(portfolios));
    }

    public addAllocationsToComparedEntities(allocations: OperationSimulationUpdatingPortfolio[]) {
        return this.store.dispatch(new AddAllocationsToComparedEntitiesAction(allocations));
    }

    public addCurrentAllocationToComparedEntities(allocations: OperationSimulationUpdatingPortfolio[]) {
        return this.store.dispatch(new AddCurrentAllocationToComparedEntitiesAction(allocations));
    }

    public addCreatingPortfolioToComparedEntities(
        portfolio: CreatePortfolio,
        allocation: SimulationCommonReferenceRequest,
        initialAllocation: ProjectionAllocation | undefined,
    ) {

        return this.store.dispatch(new AddCreatingPortfolioToComparedEntitiesAction(portfolio, allocation, initialAllocation));
    }

    public deleteComparedEntity(entityId: number, entityType: EntityType) {
        return this.store.dispatch(new DeleteComparedEntityAction(entityId, entityType));
    }

    public deleteAllComparedEntities() {
        return this.store.dispatch(new DeleteAllComparedEntitiesAction());
    }

    public reset() {
        return this.store.dispatch(new ResetAction());
    }

    public toggleBenchmark(benchmark: SimulationCommonBenchmarkParam) {
        return this.store.dispatch(new ToggleBenchmarkAction(benchmark));
    }

    public launchSimulation() {
        return this.store.dispatch(new LaunchSimulationAction());
    }

    public cancelLaunchSimulation() {
        return this.store.dispatch(new CancelLaunchSimulationAction());
    }

    public copyActiveInDraft() {
        return this.store.dispatch(new CopyActiveInDraftAction());
    }

    public updateDraftPeriodParams(
        type: SimulationHistoricalPeriodType | null,
        range: {
            start: string,
            end: string,
        } | null,
        value: Period | null,
    ): Observable<unknown> {
        return this.store.dispatch(
            new UpdateDraftPeriodParamsAction(type, range, value),
        );
    }

    public resetViewModeDisplay(): Observable<unknown> {
        return this.store.dispatch(new ResetViewModeDisplayAction());
    }

    public changeViewMode(viewMode: ViewModeType): Observable<unknown> {
        return this.store.dispatch(new ChangeViewModeAction(viewMode));
    }

    public setViewModeDisplayValues(values: SimulationHistoricalViewModeValue[]): Observable<unknown> {
        return this.store.dispatch(new SetViewModeDisplayValuesAction(values));
    }

    public getDraftPeriodParamsSnapshot(): SimulationHistoricalPeriodParam {
        return this.store.selectSnapshot(SimulationHistoricalState.getDraftPeriodParams);
    }

    public getDraftComparedEntitiesWithoutDataSnapshot(): ComparedEntity[] {
        return this.store.selectSnapshot(SimulationHistoricalState.getDraftComparedEntitiesWithoutData);
    }

    public getDraftBenchmarkSnapshot() {
        return this.store.selectSnapshot(SimulationHistoricalState.getDraftBenchmark);
    }

    public getActiveBenchmarkSnapshot(): SimulationCommonBenchmarkParam {
        return this.store.selectSnapshot(SimulationHistoricalState.getActiveBenchmark);
    }

    public getActiveComparedEntitiesSnapshot(): ComparedEntity[] {
        return this.store.selectSnapshot(SimulationHistoricalState.getActiveComparedEntities);
    }

    public getActiveComparedEntitiesCountSnapshot(): number {
        return this.store.selectSnapshot(SimulationHistoricalState.getActiveComparedEntitiesCount);
    }

    public getSimulationSnapshot(): SimulationHistoricalResponse | null {
        return this.store.selectSnapshot(SimulationHistoricalState.getSimulation);
    }

    public getViewModeDisplaySnapshot() {
        return this.store.selectSnapshot(SimulationHistoricalState.getViewModeDisplay);
    }
    public getViewModeParamSnapshot() {
        return this.store.selectSnapshot(SimulationHistoricalState.getViewModeParam);
    }

    public onAddComparisonListToComparedEntitiesSuccess(): Observable<number> {
        return this.actions$.pipe(
            ofActionSuccessful(AddComparisonListToComparedEntitiesAction),
            map((action) => action.listId),
        );
    }
}
